import { SportType } from '../../court/dto/court.dto';
import { Currency } from '../../court-pricing/dto/court-pricing-rule.dto';

/**
 * One-call organization provisioning for platform staff (elevated admins):
 * org + Stripe account + location + courts with default pricing + coaches +
 * membership offers, so a prospect sees their own club already set up on the
 * first video call.
 *
 * Every collection is optional. `courts` and `memberships` fall back to a
 * built-in starter template when OMITTED and create nothing when sent as `[]`;
 * `coaches` has no template (a coach needs a real name) and defaults to none.
 *
 * Money units differ by field, on purpose, to match the column each one lands
 * in — read the suffix:
 *   - court `offPeakPrice` / `peakPrice` and coach `hourlyRate` are MAJOR units
 *     (euros), like `CourtAvailabilityPricing.price` and
 *     `CoachPricingRule.hourlyFees`;
 *   - membership `priceInCents` is MINOR units, like
 *     `MembershipVariant.priceInCents`.
 *
 * The limits below are exported so the service re-checks them: the route's
 * pipe is hard, but the service is the guard every future caller inherits.
 */

export const PROVISION_MAX_COURTS = 12;
export const PROVISION_MAX_COACHES = 20;
export const PROVISION_MAX_MEMBERSHIP_OFFERS = 10;
/** Whole-court price for one slot, major units. */
export const PROVISION_MAX_COURT_SLOT_PRICE = 1_000;
/** Coach hourly rate, major units. */
export const PROVISION_MAX_COACH_HOURLY_RATE = 1_000;
/** Membership price per billing period, minor units (cents). */
export const PROVISION_MAX_MEMBERSHIP_PRICE_IN_CENTS = 100_000;
export const PROVISION_MAX_BOOKING_DAYS_IN_ADVANCE = 365;
export const PROVISION_MAX_PRIVATE_COURT_BOOKINGS = 50;
/** Slot lengths an operator can pick in the dashboard's availability editor. */
export const PROVISION_SLOT_DURATIONS_MINUTES = [30, 45, 60, 90, 120] as const;
export const PROVISION_COMMITMENT_MONTHS = [1, 3, 6, 12] as const;

/**
 * Upper bound on availability rows one request may create (each also gets a
 * pricing row, all in ONE transaction). The default setup — 2 padel courts —
 * is ~7,300; 12 padel courts ~44,000. 12 courts of 30-minute slots open 24 h
 * would be ~420,000 and outlast the App Service request timeout.
 */
export const PROVISION_MAX_TOTAL_SLOTS = 100_000;

export type ProvisionSportPricingDefault = {
  slotDurationMinutes: number;
  /** Whole-court price for one slot, MAJOR units. */
  offPeakPrice: number;
  /** Whole-court price for one slot, MAJOR units. */
  peakPrice: number;
};

/**
 * Starter price grid — placeholders the club edits during onboarding, sized to
 * the French market (padel ≈ €9–12 per player for 90 minutes). Lives in the DTO
 * so the dashboard form pre-fills the exact values the API would apply.
 */
export const DEFAULT_SPORT_PRICING: Partial<
  Record<SportType, ProvisionSportPricingDefault>
> = {
  [SportType.PADEL]: {
    slotDurationMinutes: 90,
    offPeakPrice: 36,
    peakPrice: 48,
  },
  [SportType.PADEL1V1]: {
    slotDurationMinutes: 60,
    offPeakPrice: 16,
    peakPrice: 22,
  },
  [SportType.TENNIS]: {
    slotDurationMinutes: 60,
    offPeakPrice: 15,
    peakPrice: 20,
  },
};
/** Any sport without an entry above. */
export const FALLBACK_SPORT_PRICING: ProvisionSportPricingDefault = {
  slotDurationMinutes: 60,
  offPeakPrice: 20,
  peakPrice: 30,
};

export class ProvisionLocationInputDto {
  /** Defaults to the organization name. */
  name?: string;

  address?: string;

  city?: string;

  postalCode?: string;

  /** Defaults to "France". */
  country?: string;

  /** IANA zone, defaults to "Europe/Paris". Validated in the service. */
  timezone?: string;
}

export class ProvisionCourtInputDto {
  name: string;

  sport: SportType;

  isOutdoor?: boolean;
}

/**
 * Per-sport override of the starter price grid. A sport with no entry uses the
 * built-in default for that sport (see `DEFAULT_SPORT_PRICING`).
 */
export class ProvisionSportPricingInputDto {
  sport: SportType;

  slotDurationMinutes: number;

  /** Whole-court price for one slot outside peak hours, MAJOR units. */
  offPeakPrice: number;

  /** Whole-court price for one slot during peak hours, MAJOR units. */
  peakPrice: number;
}

/**
 * Opening hours, as wall-clock minutes from local midnight in the location's
 * timezone (480 = 08:00, 1440 = midnight). Every block must divide evenly into
 * each sport's slot length — the service rejects a schedule that would leave a
 * short trailing slot sold at the full slot price.
 */
export class ProvisionScheduleInputDto {
  /** Defaults to 480 (08:00). */
  openingMinuteOfDay?: number;

  /** Defaults to 1380 (23:00). */
  closingMinuteOfDay?: number;

  /**
   * Monday–Friday, when peak pricing starts. Defaults to 1020 (17:00).
   * `null` = no weekday peak.
   */
  weekdayPeakStartMinuteOfDay?: number | null;

  /** Saturday and Sunday priced at peak all day. Defaults to true. */
  weekendAllDayPeak?: boolean;
}

export class ProvisionCoachInputDto {
  firstName: string;

  lastName: string;

  /** Defaults to every sport the provisioned courts offer. */
  sports?: SportType[];

  /** Private lesson, one participant, per hour, MAJOR units. Defaults to 50. */
  hourlyRate?: number;

  notificationEmail?: string;

  /** E.164. Validated by the coach service. */
  phone?: string;
}

/**
 * A subscription offer players can buy from the app: a membership type with
 * one recurring variant. The Stripe product/price is NOT created here — the
 * subscribe flow creates it lazily on the org's connected account
 * (`setupMembershipTypeInStripe`), so nothing touches Stripe for offers until
 * the club's account is onboarded and a player actually subscribes.
 */
export class ProvisionMembershipOfferInputDto {
  name: string;

  description?: string;

  /** Monthly price, MINOR units (cents). */
  priceInCents: number;

  /** Defaults to 1 (no commitment). */
  commitmentMonths?: number;

  /** Whole percent, defaults to 0 (the membership service floors decimals). */
  courtBookingDiscountPercent?: number;

  /** Whole percent, defaults to 0 (the membership service floors decimals). */
  coachingDiscountPercent?: number;

  /** Defaults to 14 (the free default membership type allows 7). */
  maxCourtBookingDaysInAdvance?: number;

  /** Concurrent upcoming private bookings. Defaults to 5. */
  maxPrivateCourtBookings?: number;
}

export enum ProvisionAdminAccessMode {
  /** Email an invitation link; the manager is ADMIN once they accept it. */
  INVITE = 'invite',
  /**
   * Force-add: ADMIN immediately, NO email of any kind, no account created on
   * their behalf. They sign up on the dashboard with this email and the club
   * is there (membership is matched by email). Sign-up does not verify the
   * address, so add them close to when they will sign up — until they do,
   * whoever signs up first with it gets the access.
   */
  ADD = 'add',
}

export class ProvisionOrganizationInputDto {
  name: string;

  /**
   * The club manager to hand the organization to, made ADMIN after everything
   * else is committed (see `adminAccessMode`). When omitted, add or invite
   * them later from the dashboard's team page (e.g. during the call).
   */
  adminEmail?: string;

  /** How `adminEmail` gets access. Defaults to `invite`. */
  adminAccessMode?: ProvisionAdminAccessMode;

  /** Defaults to EUR. */
  currency?: Currency;

  location?: ProvisionLocationInputDto;

  /** Omitted = two padel courts. `[]` = no courts. */
  courts?: ProvisionCourtInputDto[];

  pricing?: ProvisionSportPricingInputDto[];

  schedule?: ProvisionScheduleInputDto;

  /** Omitted = no coaches. */
  coaches?: ProvisionCoachInputDto[];

  /** Omitted = one "Premium" monthly offer. `[]` = no paid offer. */
  memberships?: ProvisionMembershipOfferInputDto[];
}

export type ProvisionedCourtDto = {
  id: number;
  name: string;
  sport: SportType;
  availabilityCount: number;
};

export type ProvisionedCoachDto = {
  id: string;
  firstName: string;
  lastName: string;
};

export type ProvisionedMembershipOfferDto = {
  membershipTypeId: string;
  variantId: string;
  name: string;
  priceInCents: number;
  currency: string;
  commitmentMonths: number;
};

export enum ProvisionStepStatus {
  DONE = 'done',
  SKIPPED = 'skipped',
  FAILED = 'failed',
}

export type ProvisionOrganizationOutputDto = {
  organization: { id: string; name: string };
  location: { id: string; name: string; timezone: string };
  courts: ProvisionedCourtDto[];
  coaches: ProvisionedCoachDto[];
  membershipOffers: ProvisionedMembershipOfferDto[];
  /**
   * Standard connected account, created before the database write (the column
   * is NOT NULL). The club still has to complete Stripe onboarding from the
   * dashboard before it can take payments.
   */
  stripeAccountId: string;
  /**
   * Done after the commit. `failed` never undoes the provisioning — the reason
   * is in `warnings`; add or invite them from the team page instead.
   */
  adminAccess: {
    mode: ProvisionAdminAccessMode;
    status: ProvisionStepStatus;
    email: string | null;
  };
  /** Human-readable notes for any step that did not fully succeed. */
  warnings: string[];
};
